/**
 * Parses product description rich text and extracts sections:
 * - Highlights (standalone)
 * - Fit suggestion (standalone)
 * - Materials And Washing Directions (accordion)
 * - Description (accordion - remaining content)
 */
(function () {
  'use strict';

  // Helper function to split materials/washing content
  // Separates list content (stays in materials) from paragraph content (goes to description)
  function splitMaterialsContent(sectionHtml) {
    if (!sectionHtml || !sectionHtml.trim()) {
      return { materialsContent: '', descriptionContent: '' };
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sectionHtml;

    // Find the heading first (should be at the start)
    const heading = tempDiv.querySelector('h1, h2, h3, h4, h5, h6');

    // Find all list elements (ul, ol)
    const lists = Array.from(tempDiv.querySelectorAll('ul, ol'));

    // Build materials content: heading + all lists
    let materialsContent = '';
    if (heading) {
      materialsContent = heading.outerHTML;
    }

    // Collect all list content
    lists.forEach((list) => {
      materialsContent += list.outerHTML;
    });

    // Also check for standalone list items (li without parent ul/ol)
    const allListItems = tempDiv.querySelectorAll('li');
    allListItems.forEach((li) => {
      // Only include if not already in a captured list
      const parentList = li.closest('ul, ol');
      if (!parentList || !lists.includes(parentList)) {
        // This is a standalone li - check if we should include it
        // If it's not in materials yet, it might be a list item we missed
        const liText = li.textContent.trim();
        if (
          liText &&
          liText.length > 0 &&
          !materialsContent.includes(li.outerHTML)
        ) {
          // Wrap in ul if not already wrapped
          materialsContent += '<ul>' + li.outerHTML + '</ul>';
        }
      }
    });

    // Build description content: everything after the lists
    let descriptionContent = '';

    // Find the last list to determine where description starts
    let lastListIndex = -1;
    const allChildren = Array.from(tempDiv.children);

    allChildren.forEach((child, index) => {
      if (
        child.tagName === 'UL' ||
        child.tagName === 'OL' ||
        child.querySelector('ul, ol')
      ) {
        lastListIndex = index;
      }
    });

    // Collect everything after the last list
    if (lastListIndex !== -1) {
      for (let i = lastListIndex + 1; i < allChildren.length; i++) {
        const el = allChildren[i];
        // Skip the heading (already in materialsContent)
        if (el !== heading) {
          // Only include paragraphs - exclude any lists, list items, or elements containing lists
          const hasListItems =
            el.querySelector('ul, ol, li') ||
            el.tagName === 'UL' ||
            el.tagName === 'OL' ||
            el.tagName === 'LI';

          // Only include paragraph elements that don't contain lists
          // Exclude any element that looks like a list item (starts with bullet-like content)
          if (!hasListItems && el.tagName === 'P') {
            const pText = el.textContent.trim();
            // Exclude if it looks like a list item (starts with common list patterns)
            const looksLikeListItem =
              pText.match(/^[\d\-\•\*\+]\s/) || // Starts with bullet/dash/number
              pText.match(/^[A-Z][^\.]*:\s*$/) || // Pattern like "Item: "
              (pText.length < 100 && !pText.includes('.')); // Short text without period

            if (!looksLikeListItem) {
              descriptionContent += el.outerHTML;
            }
          } else if (
            !hasListItems &&
            el.tagName !== 'UL' &&
            el.tagName !== 'OL' &&
            el.tagName !== 'LI'
          ) {
            // Include other elements (divs, etc.) but only if they contain substantial text and no lists
            const elText = el.textContent.trim();
            // Check if it's substantial descriptive text (not a list item)
            const isDescriptiveText =
              elText.length > 100 &&
              !elText.match(/^[\d\-\•\*\+]/) &&
              elText.includes('.'); // Contains periods (sentences)

            if (isDescriptiveText) {
              descriptionContent += el.outerHTML;
            }
          }
        }
      }
    }

    // Fallback: if we didn't find lists but have paragraphs, check paragraph structure
    if (!descriptionContent && lists.length === 0) {
      const paragraphs = tempDiv.querySelectorAll('p');
      paragraphs.forEach((p) => {
        const pText = p.textContent.trim();
        // If paragraph is substantial text (not a list marker), it might be description
        if (pText.length > 50 && !pText.match(/^[\d\-\•\*]/)) {
          // Check if it's structured like a list item or actual paragraph
          const isListItem =
            pText.match(/^[A-Z][^\.]*:[\s\n]/) || // "Item: description" pattern
            pText.split('\n').length > 2; // Multi-line might be list-like

          if (!isListItem && !heading?.contains(p)) {
            descriptionContent += p.outerHTML;
          }
        }
      });
    }

    return {
      materialsContent: materialsContent.trim(),
      descriptionContent: descriptionContent.trim(),
    };
  }

  function parseDescriptionContent(descriptionHtml) {
    if (!descriptionHtml || !descriptionHtml.trim()) {
      return {
        highlights: '',
        fitSuggestion: '',
        materials: '',
        description: '',
      };
    }

    const sections = {
      highlights: '',
      fitSuggestion: '',
      materials: '',
      description: '',
    };

    // Use DOM parsing for reliable heading extraction
    // Headings can be actual h1-h6 tags OR paragraphs with bold/italic styling
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = descriptionHtml;

    // Find all possible headings: actual heading tags or paragraphs with bold/italic
    const actualHeadings = Array.from(
      tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6')
    );

    // Also find paragraphs that might be styled as headings (with bold/italic)
    const allParagraphs = Array.from(tempDiv.querySelectorAll('p'));
    const styledHeadings = allParagraphs.filter((p) => {
      const text = p.textContent.trim().toLowerCase();
      // Check if this paragraph contains bold/italic text and matches heading keywords
      const hasBoldItalic = p.querySelector('b, strong, i, em');
      if (!hasBoldItalic) return false;

      // Check if text matches known heading patterns
      return (
        text.includes('highlight') ||
        (text.includes('fit') && text.includes('suggestion')) ||
        text.includes('materials') ||
        text.includes('material') ||
        (text.includes('washing') && text.includes('direction')) ||
        text === 'description'
      );
    });

    // Combine actual headings and styled paragraphs, sort by DOM position
    const headings = [...actualHeadings, ...styledHeadings].sort((a, b) => {
      // Sort by position in DOM tree
      const position = a.compareDocumentPosition(b);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    if (headings.length === 0) {
      // No headings found, treat all as description
      sections.description = descriptionHtml;
      return sections;
    }

    // Process each heading section using DOM structure
    headings.forEach((heading, index) => {
      const headingText = heading.textContent.trim().toLowerCase();

      // Extract content between this heading and the next heading using DOM
      let sectionContent = '';
      const nextHeading = headings[index + 1];

      // Collect all next siblings until we hit the next heading
      let currentNode = heading.nextSibling;
      const contentNodes = [];

      while (currentNode) {
        // Stop if we've reached the next heading
        if (nextHeading && currentNode === nextHeading) {
          break;
        }

        // Check if current node contains the next heading
        if (nextHeading && currentNode.nodeType === Node.ELEMENT_NODE) {
          if (currentNode.contains && currentNode.contains(nextHeading)) {
            // This node contains the next heading, stop here
            break;
          }
        }

        // Collect this node
        contentNodes.push(currentNode);
        currentNode = currentNode.nextSibling;
      }

      // Convert collected nodes to HTML
      if (contentNodes.length > 0) {
        const contentDiv = document.createElement('div');
        contentNodes.forEach((node) => {
          // Clone the node to avoid removing it from the DOM
          const clonedNode = node.cloneNode(true);
          contentDiv.appendChild(clonedNode);
        });
        sectionContent = contentDiv.innerHTML;
      }

      // Build section HTML with heading + content
      const sectionHtml = (heading.outerHTML + sectionContent).trim();

      if (headingText.includes('highlight')) {
        sections.highlights = sectionHtml;
      } else if (
        headingText.includes('fit suggestion') ||
        (headingText.includes('fit') && headingText.includes('suggestion'))
      ) {
        sections.fitSuggestion = sectionHtml;
      } else if (
        headingText.includes('materials') ||
        headingText.includes('material') ||
        headingText.includes('washing') ||
        headingText.includes('washing directions')
      ) {
        // Handle materials and/or washing - they might be combined
        // Split content: keep lists/structured content in materials,
        // move paragraphs/text content to description
        const { materialsContent, descriptionContent } =
          splitMaterialsContent(sectionHtml);

        if (materialsContent && materialsContent.trim()) {
          if (sections.materials) {
            sections.materials += '\n\n' + materialsContent;
          } else {
            sections.materials = materialsContent;
          }
        }

        // Add paragraph content to description
        if (descriptionContent && descriptionContent.trim()) {
          if (sections.description) {
            sections.description += '\n\n' + descriptionContent;
          } else {
            sections.description = descriptionContent;
          }
        }
      } else {
        // Any content that doesn't match highlights, fit suggestion, or materials goes into description
        // This includes paragraphs, lists, or any other headings
        if (sections.description) {
          sections.description += '\n\n';
        }
        sections.description += sectionHtml;
      }
    });

    // Also check for any content before the first heading (if it exists)
    // This is typically the main product description paragraph
    if (headings.length > 0) {
      const firstHeading = headings[0];
      let beforeFirstHeading = '';

      // Get all siblings before the first heading
      let currentElement = firstHeading.previousSibling;
      while (currentElement) {
        if (currentElement.nodeType === Node.ELEMENT_NODE) {
          beforeFirstHeading = currentElement.outerHTML + beforeFirstHeading;
        } else if (currentElement.nodeType === Node.TEXT_NODE) {
          const text = currentElement.textContent.trim();
          if (text) {
            beforeFirstHeading = '<p>' + text + '</p>' + beforeFirstHeading;
          }
        }
        currentElement = currentElement.previousSibling;
      }

      if (beforeFirstHeading.trim()) {
        // Check if it's actual content (not just whitespace/empty)
        const beforeContent = beforeFirstHeading.replace(/<[^>]*>/g, '').trim();
        if (beforeContent && beforeContent.length > 0) {
          if (sections.description) {
            sections.description =
              beforeFirstHeading.trim() + '\n\n' + sections.description;
          } else {
            sections.description = beforeFirstHeading.trim();
          }
        }
      }

      // Content after headings is already handled in the forEach loop above
      // The last heading's content is captured up to the end of the document
    }

    // Clean up sections and remove duplicates
    Object.keys(sections).forEach((key) => {
      if (sections[key]) {
        sections[key] = sections[key].trim();

        // Remove duplicate content if it exists
        if (key === 'description') {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections[key];

          // Find duplicate paragraphs
          const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
          const seenTexts = new Set();

          paragraphs.forEach((p) => {
            const pText = p.textContent.trim();
            if (seenTexts.has(pText)) {
              // Remove duplicate
              p.remove();
            } else if (pText.length > 0) {
              seenTexts.add(pText);
            }
          });

          // Also check for duplicate divs/elements with same text content
          const allElements = Array.from(tempDiv.children);
          const seenContents = new Set();
          allElements.forEach((el) => {
            const elText = el.textContent.trim();
            if (seenContents.has(elText)) {
              // Remove duplicate
              el.remove();
            } else if (elText.length > 0) {
              seenContents.add(elText);
            }
          });

          sections[key] = tempDiv.innerHTML.trim();
        }
      }
    });

    return sections;
  }

  function renderParsedDescription() {
    const containers = document.querySelectorAll(
      '.product__description-parsed[data-description-content]'
    );

    containers.forEach((container) => {
      // Reset processed flag if description content changed
      const currentContent = container.dataset.descriptionContent;
      if (container.dataset.lastContent !== currentContent) {
        container.dataset.processed = 'false';
        container.dataset.lastContent = currentContent;
      }

      if (container.dataset.processed === 'true') return;

      const descriptionHtml = container.dataset.descriptionContent;
      if (!descriptionHtml) {
        container.style.display = 'none';
        return;
      }

      // Decode the HTML
      let decodedHtml;
      try {
        decodedHtml = decodeURIComponent(descriptionHtml);
      } catch (e) {
        // If decoding fails, try direct assignment
        decodedHtml = descriptionHtml;
      }

      // Debug logging
      if (window.location.search.includes('debug=description')) {
        console.log('Raw description HTML:', descriptionHtml.substring(0, 200));
        console.log('Decoded description HTML:', decodedHtml.substring(0, 200));
      }

      // Parse the description
      const sections = parseDescriptionContent(decodedHtml);

      // Debug logging
      if (window.location.search.includes('debug=description')) {
        console.log('Sections extracted:', sections);
        console.log('Has highlights:', !!sections.highlights);
        console.log('Has fit suggestion:', !!sections.fitSuggestion);
        console.log('Has materials:', !!sections.materials);
        console.log('Has description:', !!sections.description);
      }

      // Helper to remove heading from content
      // Removes both actual heading tags (h1-h6) and styled paragraph headings
      const removeHeadingFromContent = (html) => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Remove actual heading tags first
        const actualHeading = tempDiv.querySelector('h1, h2, h3, h4, h5, h6');
        if (actualHeading) {
          actualHeading.remove();
        }

        // Also remove styled paragraph headings (p tags with bold/italic that match heading patterns)
        const allParagraphs = Array.from(tempDiv.querySelectorAll('p'));
        allParagraphs.forEach((p) => {
          const text = p.textContent.trim().toLowerCase();
          // Check if this paragraph contains bold/italic text and matches heading keywords
          const hasBoldItalic = p.querySelector('b, strong, i, em');
          if (hasBoldItalic) {
            // Check if text matches known heading patterns
            const isHeading =
              text.includes('highlight') ||
              (text.includes('fit') && text.includes('suggestion')) ||
              text.includes('materials') ||
              text.includes('material') ||
              (text.includes('washing') && text.includes('direction')) ||
              text === 'description';

            if (isHeading) {
              p.remove();
            }
          }
        });

        return tempDiv.innerHTML;
      };

      // Render Highlights
      const highlightsContent = container.querySelector(
        '[data-section-content="highlights"]'
      );
      const highlightsSection = container.querySelector(
        '[data-section="highlights"]'
      );
      if (highlightsContent && highlightsSection && sections.highlights) {
        highlightsContent.innerHTML = removeHeadingFromContent(
          sections.highlights
        );
        highlightsSection.style.display = 'flex';
      } else if (highlightsSection && !sections.highlights) {
        highlightsSection.style.display = 'none';
      }

      // Render Fit Suggestion
      const fitContent = container.querySelector(
        '[data-section-content="fit-suggestion"]'
      );
      const fitSection = container.querySelector(
        '[data-section="fit-suggestion"]'
      );
      if (fitContent && fitSection && sections.fitSuggestion) {
        fitContent.innerHTML = removeHeadingFromContent(sections.fitSuggestion);
        fitSection.style.display = 'flex';
      } else if (fitSection && !sections.fitSuggestion) {
        fitSection.style.display = 'none';
      }

      // Render Materials (remove heading from content, like Highlights and Fit Suggestion)
      const materialsContent = container.querySelector(
        '[data-section="materials"] .product__description-accordion-content'
      );
      const materialsAccordion = container.querySelector(
        '[data-section="materials"]'
      );
      if (materialsContent && materialsAccordion && sections.materials) {
        // Remove the heading from materials content (heading is shown in accordion button)
        const cleanedMaterials = removeHeadingFromContent(sections.materials);
        // Also ensure we remove any paragraphs that were split out (they should only be in description)
        const materialsDiv = document.createElement('div');
        materialsDiv.innerHTML = cleanedMaterials;
        // Remove any paragraph elements from materials (they should only be in description)
        const paragraphs = materialsDiv.querySelectorAll('p');
        paragraphs.forEach((p) => {
          const pText = p.textContent.trim();
          // Remove substantial paragraphs (likely descriptive text, not list-related)
          if (pText.length > 50) {
            p.remove();
          }
        });
        materialsContent.innerHTML = materialsDiv.innerHTML;
        materialsAccordion.style.display = 'block';
      } else if (materialsAccordion && !sections.materials) {
        materialsAccordion.style.display = 'none';
      }

      // Render Description (remaining content - remove heading if present)
      const descriptionContent = container.querySelector(
        '[data-section="description"] .product__description-accordion-content'
      );
      const descriptionAccordion = container.querySelector(
        '[data-section="description"]'
      );

      // Debug: Log if description exists
      if (window.location.search.includes('debug=description')) {
        console.log('=== DESCRIPTION SECTION DEBUG ===');
        console.log('Full parsed sections:', sections);
        console.log('Description section exists:', !!sections.description);
        console.log(
          'Description content:',
          sections.description ? sections.description.substring(0, 200) : 'null'
        );
        console.log(
          'Description content length:',
          sections.description ? sections.description.length : 0
        );
        console.log(
          'Description trimmed length:',
          sections.description ? sections.description.trim().length : 0
        );
        console.log('Description accordion element:', descriptionAccordion);
        console.log('Description content element:', descriptionContent);
      }

      if (descriptionContent && descriptionAccordion) {
        // Check for any description content - even minimal
        const hasDescriptionContent =
          sections.description &&
          sections.description.trim() &&
          sections.description.trim().length > 0;

        if (hasDescriptionContent) {
          // Remove any heading from description content (heading is shown in accordion button)
          let cleanedDescription = removeHeadingFromContent(
            sections.description
          );

          // Also remove any list items that might have snuck into description
          const descDiv = document.createElement('div');
          descDiv.innerHTML = cleanedDescription;

          // Remove all list elements (ul, ol, li) from description
          descDiv.querySelectorAll('ul, ol, li').forEach((listEl) => {
            listEl.remove();
          });

          // Also remove any paragraphs that look like list items
          descDiv.querySelectorAll('p').forEach((p) => {
            const pText = p.textContent.trim();
            // Remove if it looks like a list item
            const looksLikeListItem =
              pText.match(/^[\d\-\•\*\+]\s/) || // Starts with bullet/dash/number
              (pText.length < 100 && !pText.includes('.')) || // Short text without period
              pText.match(/^[A-Z][^\.]*:\s*$/); // Pattern like "Item: "

            if (looksLikeListItem) {
              p.remove();
            }
          });

          // Remove duplicate paragraphs
          const paragraphs = Array.from(descDiv.querySelectorAll('p'));
          const seenParagraphs = new Set();

          paragraphs.forEach((p) => {
            const pText = p.textContent.trim();
            if (seenParagraphs.has(pText)) {
              // Remove duplicate paragraph
              p.remove();
            } else if (pText.length > 0) {
              seenParagraphs.add(pText);
            }
          });

          // Also remove duplicate elements (divs, etc.) with same text content
          const allElements = Array.from(descDiv.children);
          const seenContents = new Set();

          allElements.forEach((el) => {
            const elText = el.textContent.trim();
            if (seenContents.has(elText)) {
              // Remove duplicate element
              el.remove();
            } else if (elText.length > 0) {
              seenContents.add(elText);
            }
          });

          cleanedDescription = descDiv.innerHTML.trim();

          // Check if cleaned description has actual content
          const hasCleanedContent =
            cleanedDescription &&
            cleanedDescription.trim() &&
            cleanedDescription.trim().length > 0;

          if (hasCleanedContent) {
            descriptionContent.innerHTML = cleanedDescription;
            // Remove inline style that's hiding it, then set to block
            descriptionAccordion.style.removeProperty('display');
            descriptionAccordion.style.display = 'block';

            if (window.location.search.includes('debug=description')) {
              console.log('✓ Description accordion set to display block');
              console.log(
                'Cleaned description length:',
                cleanedDescription.trim().length
              );
              console.log(
                'Cleaned description preview:',
                cleanedDescription.substring(0, 200)
              );
            }
          } else {
            descriptionAccordion.style.display = 'none';
            if (window.location.search.includes('debug=description')) {
              console.log(
                '✗ Description accordion hidden - cleaned content is empty'
              );
              console.log(
                'Original description:',
                sections.description.substring(0, 200)
              );
              console.log('Cleaned description:', cleanedDescription);
            }
          }
        } else {
          descriptionAccordion.style.display = 'none';
          if (window.location.search.includes('debug=description')) {
            console.log(
              '✗ Description accordion hidden - no description content'
            );
            console.log('Sections summary:', {
              highlights: !!sections.highlights,
              fitSuggestion: !!sections.fitSuggestion,
              materials: !!sections.materials,
              description: !!sections.description,
              descriptionLength: sections.description
                ? sections.description.length
                : 0,
            });
          }
        }
      } else {
        if (window.location.search.includes('debug=description')) {
          console.error(
            '✗ Description accordion or content element not found!'
          );
          console.log('Accordion found:', !!descriptionAccordion);
          console.log('Content found:', !!descriptionContent);
        }
      }

      // Mark as processed
      container.dataset.processed = 'true';
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderParsedDescription);
  } else {
    renderParsedDescription();
  }

  // Re-render after product swap (for color swatch switching)
  document.addEventListener('product-info:loaded', () => {
    // Reset processed flags
    document
      .querySelectorAll('.product__description-parsed')
      .forEach((container) => {
        container.dataset.processed = 'false';
      });
    setTimeout(renderParsedDescription, 100);
  });

  // Also listen for mutation observer in case description is updated dynamically
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.classList.contains('product__description-parsed') ||
              node.querySelector('.product__description-parsed'))
          ) {
            setTimeout(renderParsedDescription, 100);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
